import React from 'react';
import AppsLayout from './AppsLayout';
import Reveal from './components/Reveal';
import Badge from './components/Badge';
import Button from './components/Button';
import { birdify } from '../../content/birdify/app';
import { support } from '../../content/support';
import { trackClick } from '../../firebase/analytics';
import { APPS_ROUTES } from './routes';
import GooglePlayBadge from './components/GooglePlayBadge';
import store01Listen1620 from '../../assets/images/birdify/store-01-listen-1620.png';
import store01Listen810 from '../../assets/images/birdify/store-01-listen-810.png';
import store02LiveListening1620 from '../../assets/images/birdify/store-02-live-listening-1620.png';
import store02LiveListening810 from '../../assets/images/birdify/store-02-live-listening-810.png';
import store03DetectionResults1620 from '../../assets/images/birdify/store-03-detection-results-1620.png';
import store03DetectionResults810 from '../../assets/images/birdify/store-03-detection-results-810.png';
import store04History1620 from '../../assets/images/birdify/store-04-history-1620.png';
import store04History810 from '../../assets/images/birdify/store-04-history-810.png';
import store05SessionDetail1620 from '../../assets/images/birdify/store-05-session-detail-1620.png';
import store05SessionDetail810 from '../../assets/images/birdify/store-05-session-detail-810.png';
import store06BirdDetail1620 from '../../assets/images/birdify/store-06-bird-detail-1620.png';
import store06BirdDetail810 from '../../assets/images/birdify/store-06-bird-detail-810.png';
import store07Settings1620 from '../../assets/images/birdify/store-07-settings-1620.png';
import store07Settings810 from '../../assets/images/birdify/store-07-settings-810.png';

const PAGE = 'apps_birdify';

const SCREENSHOTS = [
  {
    id: 'store-01-listen',
    title: 'Discover birds by sound',
    summary: 'Record a sound or import audio and video for on-device identification.',
    src: store01Listen1620,
    srcSet: `${store01Listen810} 810w, ${store01Listen1620} 1620w`,
    alt: 'Birdify listing screenshot showing the Listen screen with ready state, waveform, start listening control, and import audio or video action.',
  },
  {
    id: 'store-02-live-listening',
    title: 'Live listening workflow',
    summary: 'See microphone state, timer and nearby matches while recording.',
    src: store02LiveListening1620,
    srcSet: `${store02LiveListening810} 810w, ${store02LiveListening1620} 1620w`,
    alt: 'Birdify listing screenshot showing microphone active state, recording controls, elapsed time, and nearby species matches.',
  },
  {
    id: 'store-03-detection-results',
    title: 'Review and save results',
    summary: 'Inspect likely matches, then choose Save or Discard.',
    src: store03DetectionResults1620,
    srcSet: `${store03DetectionResults810} 810w, ${store03DetectionResults1620} 1620w`,
    alt: 'Birdify listing screenshot showing detection results with confidence, attribution, and save and discard actions.',
  },
  {
    id: 'store-04-history',
    title: 'Detection history',
    summary: 'Browse recent saved sessions with species and durations.',
    src: store04History1620,
    srcSet: `${store04History810} 810w, ${store04History1620} 1620w`,
    alt: 'Birdify listing screenshot showing detection history with grouped saved sessions and species chips.',
  },
  {
    id: 'store-05-session-detail',
    title: 'Session detail and replay',
    summary: 'Revisit detected species and replay saved session audio.',
    src: store05SessionDetail1620,
    srcSet: `${store05SessionDetail810} 810w, ${store05SessionDetail1620} 1620w`,
    alt: 'Birdify listing screenshot showing a detection session detail with species result and session recording playback control.',
  },
  {
    id: 'store-06-bird-detail',
    title: 'Bird detail and context',
    summary: 'Explore species photos, confidence and online reference text.',
    src: store06BirdDetail1620,
    srcSet: `${store06BirdDetail810} 810w, ${store06BirdDetail1620} 1620w`,
    alt: 'Birdify listing screenshot showing Blue Jay details with photo attribution, confidence, and Wikipedia overview.',
  },
  {
    id: 'store-07-settings',
    title: 'Offline resource controls',
    summary: 'Manage on-device identification resources in settings.',
    src: store07Settings1620,
    srcSet: `${store07Settings810} 810w, ${store07Settings1620} 1620w`,
    alt: 'Birdify listing screenshot showing settings with on-device privacy explanation and downloaded resource controls.',
  },
];

export default function BirdifyPage() {
  const app = birdify;
  return (
    <AppsLayout
      theme="canopy"
      title="Birdify · Bird identification on your device · Keval Langalia"
      description="Birdify is a free educational Android app that identifies birds from sound on your device with BirdNET, keeps your sessions private, and adds optional online bird information."
      canonicalPath={APPS_ROUTES.birdify}
      breadcrumbs={[{ label: 'Portfolio', to: APPS_ROUTES.apps, id: 'apps' }, { label: app.name }]}
      page={PAGE}
      support={false}
    >
      <Reveal as="section" className="apps-hero apps-birdify-hero" aria-labelledby="birdify-heading">
        <div className="apps-birdify-hero-copy">
          <p className="apps-eyebrow">{app.name} · {app.platform}</p>
          <h1 id="birdify-heading" className="apps-title">{app.tagline}</h1>
          <p className="apps-lead">{app.summary}</p>
          <ul className="apps-badges" aria-label="Birdify details">
            {app.heroBadges.map((badge) => (
              <li key={badge.label}><Badge variant={badge.variant}>{badge.label}</Badge></li>
            ))}
          </ul>
          <div className="apps-hero-actions apps-hero-actions--store">
            <GooglePlayBadge
              href={app.release.playStoreUrl}
              appName={app.name}
              onClick={trackClick(PAGE, 'hero_google_play_button')}
            />
          </div>
        </div>
        <figure className="apps-screenshot apps-screenshot--hero">
          <img
            src={SCREENSHOTS[0].src}
            srcSet={SCREENSHOTS[0].srcSet}
            sizes="(max-width: 44rem) 72vw, 24rem"
            width="1620"
            height="2880"
            alt={SCREENSHOTS[0].alt}
            loading="eager"
          />
          <figcaption>{SCREENSHOTS[0].title}</figcaption>
        </figure>
      </Reveal>

      <section className="apps-section" aria-labelledby="birdify-screenshots-heading">
        <h2 id="birdify-screenshots-heading" className="apps-section-title">See Birdify in action</h2>
        <p className="apps-muted">
          These are Play Store listing screenshots prepared from authentic Birdify captures (September 2026).
        </p>
        <ul className="apps-screenshot-grid" aria-label="Birdify screenshot gallery">
          {SCREENSHOTS.slice(1).map((shot, index) => (
            <Reveal as="li" key={shot.id} delay={index * 70}>
              <figure className="apps-screenshot">
                <img
                  src={shot.src}
                  srcSet={shot.srcSet}
                  sizes="(max-width: 37.5rem) 88vw, (max-width: 70rem) 42vw, 20rem"
                  width="1620"
                  height="2880"
                  alt={shot.alt}
                  loading="lazy"
                />
                <figcaption>
                  <strong>{shot.title}</strong>
                  <span>{shot.summary}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="apps-section" aria-labelledby="birdify-features-heading">
        <h2 id="birdify-features-heading" className="apps-section-title">What Birdify does</h2>
        <p className="apps-muted">Bird identification is local. Online features are optional and clearly described.</p>
        <ul className="apps-features">
          {app.features.map((feature, index) => (
            <Reveal as="li" key={feature.id} className={`apps-feature${feature.optional ? ' apps-feature--optional' : ''}`} delay={index * 70}>
              <h3>
                {feature.title}
                {feature.optional ? <> <Badge variant="river">Optional · online</Badge></> : null}
              </h3>
              <p>{feature.body}</p>
            </Reveal>
          ))}
        </ul>
      </section>

      <section className="apps-section" id="support" aria-labelledby="birdify-support-heading">
        <h2 id="birdify-support-heading" className="apps-section-title">Support and common questions</h2>
        <p>
          Contact {app.operator} at <a href={`mailto:${app.contactEmail}`} onClick={trackClick(PAGE, 'support_email_link')}>{app.contactEmail}</a> for app support or privacy questions. Include the app version, Android version and a short description of the issue if relevant. Please avoid sending recordings, exact locations or other sensitive information unless needed to explain the issue.
        </p>
        <ul className="apps-faq">
          {app.faq.map((entry) => (
            <li key={entry.question}>
              <h3>{entry.question}</h3>
              <p>{entry.answer}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="apps-section" aria-labelledby="birdify-about-heading">
        <h2 id="birdify-about-heading" className="apps-section-title">About</h2>
        <p>
          Birdify is operated by {app.operator}. It is free and non-monetized, with no advertising or in-app purchases. Its Android package is <code>{app.packageName}</code>.
        </p>
        <p className="apps-muted">
          Development is self-funded. If Birdify is useful to you, you can support it with a coffee.
        </p>
        <Button href={support.href} external variant="text" onClick={trackClick(PAGE, support.element)}>
          <i className={support.icon} aria-hidden="true"></i> {support.label}
        </Button>
        <p className="apps-muted">Google Play and the Google Play logo are trademarks of Google LLC.</p>
      </section>

      <div className="apps-page-end">
        <Button to={app.privacyRoute} variant="secondary" onClick={trackClick(PAGE, 'privacy_policy_button')}>
          Read the privacy policy
        </Button>
      </div>
    </AppsLayout>
  );
}
